"use client";

import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIButtonRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/SDUIButtonRenderer";
import { SDUITypographyRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { Decorative } from "@faire/design-tokens";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { optimizeImageUrlWidth } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { FaireLogo } from "@faire/web/ui/logos/FaireLogo";
import React from "react";
import styled, { css } from "styled-components";

type VerticalTextImageBannerProps = {
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  onClickClientDismissHandler?: () => void;
};

export const SDUIVerticalModalTextImageBannerRenderer: React.FC<
  VerticalTextImageBannerProps
> = ({ bannerComponent, loggingData, onClickClientDismissHandler }) => {
  const {
    images,
    responsive_images,
    title,
    tagline,
    description,
    button,
    disclaimer,
  } = bannerComponent;

  const { isMobile } = useViewport();
  const isSSR = useIsSSR();

  const responsiveImages =
    convertServerResponsivePropertyToClientResponsiveProperty(
      images,
      (img) => img,
      responsive_images
    );

  const imageViewport = isMobile
    ? (responsiveImages?.mobileAndAbove ?? [])[0]
    : (responsiveImages?.desktopAndAbove ?? [])[0];

  const optimizedImageUrl = imageViewport
    ? optimizeImageUrlWidth(imageViewport, isSSR, isMobile ? 430 : 400)
    : undefined;

  return (
    <Column>
      <Background $url={isMobile ? optimizedImageUrl : undefined}>
        {isMobile ? null : <TopImage src={optimizedImageUrl} />}
        <Column
          style={{
            padding: getSpacing("5x", "4x"),
          }}
        >
          {tagline ? (
            <>
              <SDUITypographyRenderer
                textElement={tagline}
                loggingData={loggingData}
                componentStyle={{
                  hyphens: "none",
                }}
              />
              <Spacer height="3x" />
            </>
          ) : null}
          {isMobile ? (
            <>
              <FaireLogo size={104} inverse />
              <Spacer height="3x" />
            </>
          ) : null}
          {title ? (
            <>
              <SDUITypographyRenderer
                textElement={title}
                loggingData={loggingData}
                componentStyle={{
                  hyphens: "none",
                }}
              />
              <Spacer height="2x" />
            </>
          ) : null}
          {description.length > 0 ? (
            <>
              {description.map((descriptionElement, index) => (
                <React.Fragment key={index}>
                  {index > 0 ? <Spacer height="1/2x" /> : null}
                  <SDUITypographyRenderer
                    textElement={descriptionElement}
                    loggingData={loggingData}
                    componentStyle={{
                      hyphens: "none",
                    }}
                  />
                </React.Fragment>
              ))}
              <Spacer height="3x" />
            </>
          ) : null}
          {button ? (
            <div className="flex flex-col items-start md:w-full">
              <SDUIButtonRenderer
                buttonElement={button}
                className="w-full min-w-[220px]"
                loggingData={loggingData}
                fireLegacyOnClickEvent
                onClickClientDismissHandler={onClickClientDismissHandler}
                minWidth={isMobile ? "220px" : "100%"}
              />
              <Spacer height="2x" />
            </div>
          ) : null}
          {disclaimer ? (
            <>
              <SDUITypographyRenderer
                textElement={disclaimer}
                loggingData={loggingData}
                componentStyle={{
                  hyphens: "none",
                }}
              />
            </>
          ) : null}
        </Column>
      </Background>
    </Column>
  );
};

const Background = styled(Column)<{ $url?: string }>`
  ${(props) =>
    props.$url
      ? css`
          background: url(${props.$url}) center / cover no-repeat;
        `
      : css`
          background-color: ${Decorative.tint.neutral};
        `}

  ${responsiveMediaQueries.mobile} {
    min-height: 100vh;
  }
`;

const TopImage = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  object-position: 0 85%;
  height: 200px;
`;
